/**
 * Mode hors ligne des visites de prévention — stockage dans l'appareil (IndexedDB).
 *
 *  - Préparation (en ligne) : les visites choisies dans « Visites à réaliser » sont
 *    téléchargées avec leur bâtiment, leur rendez-vous, les grilles et la dernière
 *    inspection (pré-remplissage).
 *  - Terrain (sans réseau) : la visite s'ouvre depuis ces données ; chaque
 *    enregistrement est rangé dans la file d'envoi (voir fileEnvoiHorsLigne.js).
 *  - Retour du réseau : la file est envoyée automatiquement (OfflineManager).
 *
 * Tout est isolé par tenant + utilisateur : un autre compte connecté sur le même
 * appareil ne voit ni n'envoie les données d'un collègue.
 */
import { openDB } from 'idb';
import {
  traiterFile, executerOperation, estIdLocal, nouvelIdLocal, estErreurTransitoire,
} from './fileEnvoiHorsLigne';
import { cleVisite, derniereParGrille } from '../utils/visitesHorsLigne';
import {
  apiGet as apiGetServeur, apiPost, apiPut, apiPatch, apiDelete, getTenantItem,
} from '../utils/api';

const DB_NAME = 'ProFireManagerOffline';
const DB_VERSION = 2;
export const EVENEMENT_FILE = 'pfm:file-hors-ligne';

// Au-delà, le stockage est considéré indisponible (ex. ancien onglet qui bloque la mise
// à niveau de la base) : l'enregistrement part alors directement, comme avant.
const DELAI_OUVERTURE_MS = 5000;

let dbPromise = null;
export const initOfflineDB = () => {
  if (!dbPromise) {
    let ouverture = null;
    ouverture = openDB(DB_NAME, DB_VERSION, {
      // Une version plus récente de l'application veut mettre la base à niveau : on libère.
      blocking() {
        ouverture.then((db) => db.close()).catch(() => {});
        dbPromise = null;
      },
      upgrade(db) {
        // v1 (anciens magasins, conservés tels quels pour ne rien casser).
        ['batiments', 'grilles_inspection', 'plans_intervention'].forEach((nom) => {
          if (!db.objectStoreNames.contains(nom)) db.createObjectStore(nom, { keyPath: 'id' });
        });
        if (!db.objectStoreNames.contains('inspections_pending')) {
          db.createObjectStore('inspections_pending', { keyPath: 'local_id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
        }
        // v2
        if (!db.objectStoreNames.contains('visites_preparees')) {
          db.createObjectStore('visites_preparees', { keyPath: 'cle' });
        }
        if (!db.objectStoreNames.contains('inspections_locales')) {
          db.createObjectStore('inspections_locales', { keyPath: 'cle' });
        }
        if (!db.objectStoreNames.contains('file_envoi')) {
          db.createObjectStore('file_envoi', { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains('correspondances')) {
          db.createObjectStore('correspondances', { keyPath: 'local_id' });
        }
      },
    });
    const delai = new Promise((_, rejeter) => {
      setTimeout(() => rejeter(new Error('Stockage de l’appareil indisponible')), DELAI_OUVERTURE_MS);
    });
    dbPromise = Promise.race([ouverture, delai]).catch((e) => {
      dbPromise = null; // permettre une nouvelle tentative plus tard
      throw e;
    });
  }
  return dbPromise;
};

const signalerChangement = () => {
  try { window.dispatchEvent(new CustomEvent(EVENEMENT_FILE)); } catch (e) { /* noop */ }
};

const appartient = (tenant, userId) => (r) => r.tenant === tenant && r.user_id === userId;
const cleCompte = (tenant, userId, id) => `${tenant}|${userId}|${id}`;

// ---------------------------------------------------------------------------
// Préparation
// ---------------------------------------------------------------------------

/**
 * Télécharge les visites choisies. Remplace la préparation précédente de ce compte
 * (sauf `ajouter: true` : préparation d'un secteur de bâtiments, ajoutée à la précédente)
 * (les visites déjà commencées hors ligne et la file d'envoi ne sont jamais touchées).
 * @returns {{ preparees: number, echecs: Array<{nom:string, erreur:string}> }}
 */
export const preparerVisites = async ({
  tenantSlug, userId, visites, apiGet, onProgres, ajouter = false,
}) => {
  // `ajouter` : les visites déjà préparées sont GARDÉES (préparation d'un secteur de
  // bâtiments) ; sinon la préparation remplace la précédente (choix fait dans Prévention).
  if (ajouter && visites.length === 0) return { preparees: 0, echecs: [] };
  const db = await initOfflineDB();
  const get = exigerReponse((endpoint) => apiGet(tenantSlug, endpoint));
  const grilles = (await get('/prevention/grilles-inspection')) || [];

  const enregistrements = [];
  const echecs = [];
  for (let i = 0; i < visites.length; i += 1) {
    const v = visites[i];
    if (onProgres) onProgres(i, visites.length);
    try {
      const batiment = await get(`/prevention/batiments/${v.batiment_id}`);
      const inspection = v.source === 'rendez_vous' && v.inspection_id
        ? await get(`/prevention/inspections/${v.inspection_id}`)
        : null;
      let historique = [];
      try {
        historique = await get(`/prevention/inspections?batiment_id=${v.batiment_id}`);
      } catch (e) { /* pré-remplissage facultatif */ }
      enregistrements.push({
        cle: cleCompte(tenantSlug, userId, cleVisite(v)),
        tenant: tenantSlug,
        user_id: userId,
        visite: v,
        batiment,
        inspection,
        dernieres_par_grille: derniereParGrille(
          (historique || []).filter((h) => h.id !== v.inspection_id),
        ),
        prepare_le: new Date().toISOString(),
      });
    } catch (e) {
      echecs.push({
        cle: cleCompte(tenantSlug, userId, cleVisite(v)),
        nom: v.nom || v.adresse || v.batiment_id,
        erreur: e?.message || String(e),
      });
    }
  }
  if (visites.length > 0 && enregistrements.length === 0) {
    // Rien n'a pu être téléchargé (coupure ?) : la préparation précédente reste intacte.
    throw new Error(echecs[0]?.erreur || 'Aucune visite téléchargée');
  }

  // Écriture en une transaction, une fois tout téléchargé (une préparation
  // interrompue par une coupure ne détruit pas la précédente).
  const tx = db.transaction(['visites_preparees', 'grilles_inspection', 'metadata'], 'readwrite');
  const vp = tx.objectStore('visites_preparees');
  const anciennes = (await vp.getAll()).filter(appartient(tenantSlug, userId));
  // Une visite déjà commencée hors ligne garde son lien vers l'inspection locale.
  const commencees = Object.fromEntries(anciennes
    .filter((a) => a.inspection_locale_id)
    .map((a) => [a.cle, { inspection_locale_id: a.inspection_locale_id, realisee: !!a.realisee }]));
  // Une visite cochée mais en échec garde sa version précédente si elle en avait une.
  const enEchec = new Set(echecs.map((e) => e.cle));
  if (!ajouter) {
    await Promise.all(anciennes.filter((a) => !enEchec.has(a.cle)).map((a) => vp.delete(a.cle)));
  }
  await Promise.all(enregistrements.map((r) => vp.put({ ...r, ...(commencees[r.cle] || {}) })));
  const gs = tx.objectStore('grilles_inspection');
  const anciennesGrilles = (await gs.getAll()).filter((g) => g._tenant === tenantSlug);
  await Promise.all(anciennesGrilles.map((g) => gs.delete(g.id)));
  await Promise.all(grilles
    .filter((g) => g.type_grille !== 'vierge')
    .map((g) => gs.put({ ...g, _tenant: tenantSlug })));
  await tx.objectStore('metadata').put({
    key: cleCompte(tenantSlug, userId, 'preparation'),
    value: {
      date: new Date().toISOString(),
      nb: ajouter
        ? new Set([...anciennes.map((a) => a.cle), ...enregistrements.map((r) => r.cle)]).size
        : enregistrements.length,
    },
  });
  await tx.done;
  signalerChangement();
  return { preparees: enregistrements.length, echecs };
};

export const lirePreparation = async (tenantSlug, userId) => {
  const db = await initOfflineDB();
  const m = await db.get('metadata', cleCompte(tenantSlug, userId, 'preparation'));
  return m ? m.value : null;
};

export const listerVisitesPreparees = async (tenantSlug, userId) => {
  const db = await initOfflineDB();
  return (await db.getAll('visites_preparees')).filter(appartient(tenantSlug, userId));
};

export const grillesHorsLigne = async (tenantSlug) => {
  const db = await initOfflineDB();
  return (await db.getAll('grilles_inspection')).filter((g) => g._tenant === tenantSlug);
};

// ---------------------------------------------------------------------------
// Données de référence (inventaires de véhicules) : véhicules, formulaires
// ---------------------------------------------------------------------------

/**
 * Garde dans l'appareil la dernière version lue d'une liste du serveur (ex. véhicules),
 * pour pouvoir l'afficher sans réseau. Appelée à chaque lecture réussie.
 */
export const memoriserReference = async (tenantSlug, userId, nom, valeur) => {
  const db = await initOfflineDB();
  await db.put('metadata', {
    key: cleCompte(tenantSlug, userId, `ref:${nom}`),
    value: { valeur, maj_le: new Date().toISOString() },
  });
};

/** @returns {{ valeur: any, maj_le: string } | null} */
export const lireReference = async (tenantSlug, userId, nom) => {
  const db = await initOfflineDB();
  const m = await db.get('metadata', cleCompte(tenantSlug, userId, `ref:${nom}`));
  return m ? m.value : null;
};

export const EVENEMENT_COPIE = 'pfm:copie-hors-ligne';

// Dernière copie servie : un écran encore en « Chargement… » n'a pas de bandeau pour
// entendre l'événement — le bandeau la lit en s'affichant. Effacée (et le bandeau masqué)
// dès qu'une lecture serveur réussit.
let derniereCopie = null;
export const derniereCopieServie = () => derniereCopie;
const signalerCopie = (majLe) => {
  derniereCopie = { maj_le: majLe };
  try { window.dispatchEvent(new CustomEvent(EVENEMENT_COPIE, { detail: { maj_le: majLe } })); } catch (x) { /* noop */ }
};
const lectureServeurReussie = () => {
  if (!derniereCopie) return;
  derniereCopie = null;
  try { window.dispatchEvent(new CustomEvent(EVENEMENT_COPIE, { detail: { retour: true } })); } catch (x) { /* noop */ }
};

/** Clés des copies, une par mode de lecture (partagées avec la préparation hors ligne). */
export const cleCopieApi = (endpoint) => `get:${endpoint}`;
export const cleCopieUrl = (mode, url) => {
  const u = new URL(url, window.location.origin);
  return `${mode}:${u.pathname}${u.search}`;
};

/**
 * Lecture serveur avec copie dans l'appareil : chaque lecture réussie est gardée ; sans
 * réseau, la dernière copie est rendue (et un événement prévient l'écran, qui affiche
 * le bandeau « Hors ligne »). Sans copie, l'erreur d'origine remonte comme avant.
 */
export const apiGetAvecCopie = async (tenantSlug, userId, endpoint) => {
  try {
    const data = await apiGetServeur(tenantSlug, endpoint);
    if (data !== undefined) {
      lectureServeurReussie();
      memoriserReference(tenantSlug, userId, cleCopieApi(endpoint), data).catch(() => {});
    }
    return data;
  } catch (e) {
    if (estErreurTransitoire(e)) {
      const copie = await lireReference(tenantSlug, userId, cleCopieApi(endpoint)).catch(() => null);
      if (copie) {
        signalerCopie(copie.maj_le);
        return copie.valeur;
      }
    }
    throw e;
  }
};

/**
 * Écriture EN LIGNE SEULEMENT (jamais mise en attente) : une erreur réseau devient une
 * erreur `hors_ligne` portant un message clair (`message` : texte ou fonction). Les refus
 * du serveur et le 401 (session expirée) restent inchangés.
 */
export const enLigneSeulement = (appel, message) => async (...args) => {
  try {
    return await appel(...args);
  } catch (error) {
    if (estErreurTransitoire(error) && error.status !== 401) {
      throw Object.assign(new Error(typeof message === 'function' ? message() : message), { hors_ligne: true });
    }
    throw error;
  }
};

/**
 * Lecture quelconque (ex. `axios.get(...).then((r) => r.data)`) avec copie gardée dans
 * l'appareil sous la clé `cle` : même résultat qu'avant si le serveur répond ; sans
 * réseau, la dernière copie. Sans copie, l'erreur d'origine remonte comme avant.
 */
export const lectureAvecCopie = async (tenantSlug, cle, lire) => {
  const userId = idUtilisateurCourant();
  try {
    const data = await lire();
    lectureServeurReussie();
    memoriserReference(tenantSlug, userId, cle, data).catch(() => {});
    return data;
  } catch (e) {
    if (estErreurTransitoire(e) || [502, 503, 504].includes(e?.response?.status)) {
      const copie = await lireReference(tenantSlug, userId, cle).catch(() => null);
      if (copie) {
        signalerCopie(copie.maj_le);
        return copie.valeur;
      }
    }
    throw e;
  }
};

/**
 * Remplaçant d'`axios.get(url, config)` pour une LECTURE JSON : même réponse `{ data }`
 * si le serveur répond (copie gardée) ; sans réseau, `{ data }` de la dernière copie.
 * `axiosInstance` est passé par l'appelant (ce service n'importe pas axios).
 */
export const axiosGetAvecCopie = (axiosInstance, url, config) => {
  const u = new URL(url, window.location.origin);
  const tenantSlug = (u.pathname.match(/\/api\/([^/]+)\//) || [])[1] || '';
  return lectureAvecCopie(tenantSlug, cleCopieUrl('axios', url), () => axiosInstance.get(url, config).then((r) => r.data))
    .then((data) => ({ data }));
};

/** Compte connecté (copie du profil gardée par AuthContext) — pour isoler les copies. */
export const idUtilisateurCourant = () => {
  try { return JSON.parse(getTenantItem('user') || 'null')?.id || 'inconnu'; } catch (e) { return 'inconnu'; }
};

/**
 * Remplaçant direct de `fetch` pour une LECTURE (GET) écrite avec `fetch` brut : même
 * réponse qu'avant si le serveur répond (et copie gardée dans l'appareil) ; sans réseau
 * (ou 502/503/504), une réponse 200 construite depuis la dernière copie. Sans copie, le
 * comportement d'avant (erreur levée / réponse en échec) est inchangé.
 */
export const fetchAvecCopie = async (tenantSlug, url, init) => {
  const cle = cleCopieUrl('fetch', url);
  const userId = idUtilisateurCourant();
  const depuisCopie = async () => {
    const copie = await lireReference(tenantSlug, userId, cle).catch(() => null);
    if (!copie) return null;
    signalerCopie(copie.maj_le);
    return new Response(JSON.stringify(copie.valeur), { status: 200, headers: { 'Content-Type': 'application/json' } });
  };
  let reponse;
  try {
    reponse = await fetch(url, init);
  } catch (e) {
    if (estErreurTransitoire(e) || e?.name === 'AbortError') {
      const r = await depuisCopie();
      if (r) return r;
    }
    throw e;
  }
  if (reponse.ok) {
    lectureServeurReussie();
    // La copie est un bonus : elle ne doit jamais casser la lecture d'origine.
    try {
      if (typeof reponse.clone === 'function') {
        reponse.clone().json()
          .then((data) => memoriserReference(tenantSlug, userId, cle, data))
          .catch(() => {});
      }
    } catch (e) { /* noop */ }
  } else if ([502, 503, 504].includes(reponse.status)) {
    const r = await depuisCopie();
    if (r) return r;
  }
  return reponse;
};

/** Dernier état du rapport d'intervention saisi sur l'appareil (envois en attente). */
export const memoriserInterventionLocale = (tenantSlug, interventionId, etat) => (
  memoriserReference(tenantSlug, idUtilisateurCourant(), `intervention_locale:${interventionId}`, etat)
);
export const lireInterventionLocale = async (tenantSlug, interventionId) => (
  (await lireReference(tenantSlug, idUtilisateurCourant(), `intervention_locale:${interventionId}`))?.valeur || null
);
export const interventionAEnvoisEnAttente = (tenantSlug, interventionId) => (
  aDesEnvoisEnAttente(tenantSlug, idUtilisateurCourant(), `intervention:${interventionId}`)
);

/**
 * Un formulaire d'inspection par son id ; sans réseau ni copie de ce formulaire, il est
 * repris de la LISTE des formulaires gardée dans l'appareil (même contenu).
 */
export const formulaireAvecCopie = async (tenantSlug, userId, id) => {
  try {
    return await apiGetAvecCopie(tenantSlug, userId, `/formulaires-inspection/${id}`);
  } catch (e) {
    if (!estErreurTransitoire(e)) throw e;
    for (const nom of ['get:/formulaires-inspection', 'formulaires_inspection']) {
      const copie = await lireReference(tenantSlug, userId, nom).catch(() => null);
      const f = Array.isArray(copie?.valeur) ? copie.valeur.find((x) => x.id === id) : null;
      if (f) {
        signalerCopie(copie.maj_le);
        return f;
      }
    }
    throw e;
  }
};

/**
 * Télécharge les véhicules, les formulaires d'inventaire et les points de vérification
 * des rondes de sécurité (liste complète, filtrée par véhicule dans l'appareil).
 */
export const preparerInventaires = async ({ tenantSlug, userId, apiGet }) => {
  const get = exigerReponse((endpoint) => apiGet(tenantSlug, endpoint));
  // Chaque liste est indépendante : selon ses droits, un pompier peut se voir refuser
  // les véhicules mais pas ses EPI. On garde tout ce qui répond.
  const garder = (endpoint, nom, transformer = (x) => x) => async () => {
    const data = await get(endpoint);
    await memoriserReference(tenantSlug, userId, nom, transformer(data));
    return data;
  };
  const taches = [
    garder('/actifs/vehicules', 'vehicules', (v) => v || []),
    garder('/formulaires-inspection', 'formulaires_inspection', (f) => f || []),
    garder('/actifs/rondes-securite/points-verification', 'points_ronde_saaq', (r) => r?.points_verification || {}),
    // Équipements et EPI : mêmes copies que celles gardées à la lecture par apiGetAvecCopie.
    ...['/formulaires-inspection', '/mes-epi', '/mes-equipements', '/types-epi', '/equipements/categories',
      '/equipements', '/epi', '/parties-faciales/modeles-inspection/actif',
      // Points d'eau : carte / liste, inspections des bornes sèches.
      '/points-eau', '/points-eau-statistiques', '/bornes-seches/modeles-inspection/actif',
    ].map((endpoint) => garder(endpoint, cleCopieApi(endpoint))),
  ];
  const resultats = await Promise.allSettled(taches.map((t) => t()));
  const echec = resultats.find((r) => r.status === 'rejected');
  if (resultats.every((r) => r.status === 'rejected')) throw echec.reason;
  signalerChangement();
  const vehicules = resultats[0].status === 'fulfilled' ? resultats[0].value : [];
  return { vehicules: (vehicules || []).length };
};

// ---------------------------------------------------------------------------
// Inspections ouvertes / modifiées hors ligne
// ---------------------------------------------------------------------------

/**
 * `contexte` ({ grille, batiment }) : permet de rouvrir sans réseau une visite qui
 * n'avait pas été préparée (réseau coupé au moment d'enregistrer).
 */
export const ecrireInspectionLocale = async (tenantSlug, userId, doc, contexte = {}) => {
  const db = await initOfflineDB();
  const cle = cleCompte(tenantSlug, userId, doc.id);
  const avant = await db.get('inspections_locales', cle);
  await db.put('inspections_locales', {
    cle, tenant: tenantSlug, user_id: userId, id: doc.id, doc,
    grille: contexte.grille || avant?.grille || null,
    batiment: contexte.batiment || avant?.batiment || null,
    maj_le: new Date().toISOString(),
  });
};

const lireInspectionLocale = async (tenantSlug, userId, id) => {
  const db = await initOfflineDB();
  return (await db.get('inspections_locales', cleCompte(tenantSlug, userId, id))) || null;
};

const visitePourInspection = async (tenantSlug, userId, inspectionId) => (
  (await listerVisitesPreparees(tenantSlug, userId)).find(
    (r) => r.inspection_locale_id === inspectionId || r.visite.inspection_id === inspectionId,
  ) || null
);

/**
 * Démarre (ou reprend) une visite préparée. Retourne l'id d'inspection à ouvrir.
 * Mêmes données qu'en ligne : création d'inspection (bâtiment à inspecter) ou grille
 * posée sur le rendez-vous — envoyées plus tard par la file.
 */
export const demarrerVisitePreparee = async ({ tenantSlug, user, cle, grille }) => {
  const db = await initOfflineDB();
  const r = await db.get('visites_preparees', cle);
  if (!r) throw new Error('Visite préparée introuvable');
  if (r.inspection_locale_id) return r.inspection_locale_id;

  if (r.inspection) {
    const doc = { ...r.inspection };
    if (!doc.grille_inspection_id) {
      doc.grille_inspection_id = grille.id;
      await ajouterOperation(tenantSlug, user.id, {
        type: 'requete',
        inspection_id: doc.id,
        libelle: r.visite.nom,
        donnees: { methode: 'PATCH', endpoint: '/prevention/inspections/{id}', corps: { grille_inspection_id: grille.id } },
      });
    }
    await ecrireInspectionLocale(tenantSlug, user.id, doc);
    await db.put('visites_preparees', { ...r, inspection_locale_id: doc.id });
    return doc.id;
  }

  // Bâtiment à inspecter : même création que « Inspecter » en ligne (Prevention.jsx).
  const now = new Date();
  const corps = {
    batiment_id: r.batiment.id,
    grille_inspection_id: grille.id,
    date_inspection: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`,
    type_inspection: 'reguliere',
    preventionniste_id: user.id,
    heure_debut: now.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }),
    resultats: {},
    statut_global: 'en_cours',
  };
  const id = nouvelIdLocal();
  await ajouterOperation(tenantSlug, user.id, {
    type: 'creer_inspection', inspection_id: id, libelle: r.visite.nom, donnees: { corps },
  });
  await ecrireInspectionLocale(tenantSlug, user.id, { ...corps, id });
  await db.put('visites_preparees', { ...r, inspection_locale_id: id });
  return id;
};

/**
 * Données nécessaires au formulaire de visite, lues dans l'appareil.
 * `null` si la visite n'a pas été préparée.
 */
export const chargerContexteHorsLigne = async (tenantSlug, userId, inspectionId) => {
  const r = await visitePourInspection(tenantSlug, userId, inspectionId);
  const local = await lireInspectionLocale(tenantSlug, userId, inspectionId);
  const inspection = local?.doc || r?.inspection;
  if (!inspection) return null;
  const grille = (await grillesHorsLigne(tenantSlug)).find((g) => g.id === inspection.grille_inspection_id)
    || local?.grille;
  const batiment = r?.batiment || local?.batiment;
  if (!grille || !batiment) return null;
  return {
    inspection,
    grille,
    batiment,
    derniereInspection: r?.dernieres_par_grille?.[inspection.grille_inspection_id] || null,
  };
};

/** Marque la visite préparée comme réalisée sur l'appareil (affichage de la liste). */
export const marquerVisiteRealisee = async (tenantSlug, userId, inspectionId) => {
  const db = await initOfflineDB();
  const r = await visitePourInspection(tenantSlug, userId, inspectionId);
  if (r) await db.put('visites_preparees', { ...r, realisee: true });
  signalerChangement();
};

/** Oublie l'état local d'une visite (elle pourra être recommencée depuis la liste). */
export const reinitialiserVisiteLocale = async (tenantSlug, userId, inspectionId) => {
  const db = await initOfflineDB();
  await db.delete('inspections_locales', cleCompte(tenantSlug, userId, inspectionId));
  const r = await visitePourInspection(tenantSlug, userId, inspectionId);
  if (r) {
    const { inspection_locale_id: _x, realisee: _y, ...reste } = r;
    await db.put('visites_preparees', reste);
  }
  signalerChangement();
};

/**
 * Annule une visite démarrée hors ligne dont la création n'est pas encore partie :
 * tout reste local, on efface simplement ce qui a été mis en file pour elle.
 * @returns {boolean} true si l'annulation a été entièrement locale.
 */
export const annulerVisiteLocale = async (tenantSlug, userId, inspectionId) => {
  if (!estIdLocal(inspectionId)) return false;
  const db = await initOfflineDB();
  if (await db.get('correspondances', inspectionId)) return false; // déjà créée sur le serveur
  const ops = (await listerOperations(tenantSlug, userId)).filter((o) => o.inspection_id === inspectionId);
  await Promise.all(ops.map((o) => db.delete('file_envoi', o.id)));
  await reinitialiserVisiteLocale(tenantSlug, userId, inspectionId);
  return true;
};

// ---------------------------------------------------------------------------
// File d'envoi
// ---------------------------------------------------------------------------

export const listerOperations = async (tenantSlug, userId) => {
  const db = await initOfflineDB();
  return (await db.getAll('file_envoi'))
    .filter(appartient(tenantSlug, userId))
    .sort((a, b) => a.id - b.id);
};

export const ajouterOperation = async (tenantSlug, userId, op) => {
  const db = await initOfflineDB();
  const complet = {
    ...op, tenant: tenantSlug, user_id: userId, etape: 0, tentatives: 0, statut: 'en_attente',
    cree_le: new Date().toISOString(),
  };
  complet.id = await db.add('file_envoi', complet);
  signalerChangement();
  return complet;
};

export const retirerOperation = async (id) => {
  const db = await initOfflineDB();
  await db.delete('file_envoi', id);
  signalerChangement();
};

export const reessayerOperation = async (id) => {
  const db = await initOfflineDB();
  const op = await db.get('file_envoi', id);
  if (op) await db.put('file_envoi', { ...op, statut: 'en_attente', derniere_erreur: null });
  signalerChangement();
};

/** Des envois de cette inspection attendent-ils encore dans l'appareil ? */
export const aDesEnvoisEnAttente = async (tenantSlug, userId, cle) => (
  (await listerOperations(tenantSlug, userId)).some((o) => o.inspection_id === cle || o.cible === cle)
);

const depsStockage = async (tenantSlug, userId, api) => {
  const db = await initOfflineDB();
  return {
    lister: () => listerOperations(tenantSlug, userId),
    maj: async (op) => { await db.put('file_envoi', op); },
    supprimer: async (id) => { await db.delete('file_envoi', id); },
    lireCorrespondance: (localId) => db.get('correspondances', localId),
    ecrireCorrespondance: async (localId, valeur) => {
      await db.put('correspondances', { local_id: localId, ...valeur });
    },
    api,
  };
};

// Un seul accès à la file à la fois — envoi OU ajout/fusion — dans l'onglet (chaîne de
// promesses) et entre onglets quand le navigateur le permet (Web Locks). Sans ça, une
// fusion pourrait modifier une opération pendant qu'elle part : la modification serait
// perdue au moment où l'opération envoyée est retirée.
const avecVerrou = (fn) => {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request('pfm-file-envoi-hors-ligne', fn);
  }
  return fn();
};
let chaineFile = Promise.resolve();
const exclusif = (fn) => {
  const p = chaineFile.then(() => avecVerrou(fn));
  chaineFile = p.catch(() => {});
  return p;
};

/**
 * Envoie la file du compte. Sans danger à appeler souvent (un seul envoi à la fois).
 * `api` = { post, put, patch, del } liés au tenant.
 */
export const synchroniser = async (tenantSlug, userId, api) => {
  const bilan = await exclusif(async () => traiterFile(await depsStockage(tenantSlug, userId, api)));
  if (bilan.envoyees || bilan.refusees) signalerChangement();
  return bilan;
};

/**
 * Ajoute l'opération, ou la FUSIONNE dans la dernière opération de la file si celle-ci
 * vise la même cible (`fusion`), n'a pas commencé et attend encore : utilisé par les
 * enregistrements automatiques (PUT partiel, `$set` côté serveur) pour ne pas empiler
 * des centaines de petites requêtes pendant une coupure. L'ordre reste exact : la
 * fusion ne se fait que si rien n'a été mis en file après.
 */
export const ajouterOuFusionner = (tenantSlug, userId, op) => exclusif(async () => {
  if (op.fusion) {
    const ops = await listerOperations(tenantSlug, userId);
    const derniere = ops[ops.length - 1];
    if (derniere && derniere.fusion === op.fusion && derniere.statut === 'en_attente'
        && !derniere.etape && derniere.donnees?.methode === op.donnees.methode
        && derniere.donnees?.endpoint === op.donnees.endpoint) {
      const db = await initOfflineDB();
      const fusionnee = {
        ...derniere,
        fusionnee: true,
        donnees: { ...derniere.donnees, corps: { ...derniere.donnees.corps, ...op.donnees.corps } },
      };
      await db.put('file_envoi', fusionnee);
      signalerChangement();
      return fusionnee;
    }
  }
  return ajouterOperation(tenantSlug, userId, op);
});

/**
 * Enregistre une opération et tente de l'envoyer tout de suite.
 * @returns {{ etat: 'envoyee'|'en_file'|'refusee', reponse?, avertissements?, erreur?, op }}
 *  - envoyee : le serveur a tout reçu (comportement en ligne habituel)
 *  - en_file : pas de réseau, gardée dans l'appareil, partira automatiquement
 *  - refusee : le serveur a refusé ; l'opération est retirée (l'utilisateur corrige et
 *    réessaie) — sauf `garderSiRefusee` : elle reste alors visible dans la pastille
 *    (enregistrements automatiques : des modifications faites hors ligne y sont fusionnées).
 */
export const envoyerMaintenant = async (tenantSlug, userId, op, api, { garderSiRefusee = false } = {}) => {
  let enregistree;
  try {
    enregistree = await ajouterOuFusionner(tenantSlug, userId, op);
  } catch (e) {
    // Stockage de l'appareil indisponible (navigation privée…) : envoi direct, comme avant.
    try {
      const res = await executerOperation({ ...op, etape: 0 }, { api, lireCorrespondance: async () => null });
      return { etat: 'envoyee', ...res, op };
    } catch (err) {
      return { etat: 'refusee', erreur: err, op };
    }
  }
  let bilan;
  try {
    bilan = await synchroniser(tenantSlug, userId, api);
  } catch (e) {
    // L'opération est déjà rangée dans l'appareil : elle repartira avec la file.
    return { etat: 'en_file', erreur: e, op: enregistree };
  }
  const detail = bilan.details[enregistree.id];
  if (detail?.etat === 'envoyee') return { ...detail, op: detail.op || enregistree };
  if (detail?.etat === 'refusee') {
    // Une opération toute neuve refusée est retirée (comme avant : erreur affichée, la
    // saisie reste à l'écran). Une opération qui portait déjà des modifications faites
    // hors ligne (fusionnée, ou déjà tentée) est gardée : on ne perd jamais ces saisies.
    const porteDesSaisiesHorsLigne = enregistree.fusionnee || enregistree.tentatives > 0;
    if (!(garderSiRefusee && porteDesSaisiesHorsLigne)) await retirerOperation(enregistree.id);
    return { ...detail, op: enregistree };
  }
  return { etat: 'en_file', erreur: detail?.erreur, op: enregistree };
};

/**
 * `apiCall` ne lève PAS d'erreur sur une session expirée (401) : il renvoie `undefined`
 * et redirige vers la connexion. Pour la file, ce n'est surtout pas un succès — sinon
 * l'enregistrement serait effacé sans avoir été reçu. On le transforme en erreur 401,
 * que la file garde pour la renvoyer après la reconnexion.
 */
// Délai maximal d'un envoi de la file : sur iPhone (WKWebView), une requête peut rester
// pendue sans jamais répondre — elle bloquerait toute la file. Passé ce délai, l'envoi
// est traité comme une coupure (réessayé plus tard ; un PUT rejoué est sans effet).
const DELAI_ENVOI_MS = 60000;

export const exigerReponse = (appel, delaiMs = DELAI_ENVOI_MS) => async (endpoint, corps) => {
  let minuterie;
  const delai = new Promise((_, rejeter) => {
    minuterie = setTimeout(() => rejeter(Object.assign(new Error('Délai dépassé'), { status: 504 })), delaiMs);
  });
  let rep;
  try {
    rep = await Promise.race([appel(endpoint, corps), delai]);
  } finally {
    clearTimeout(minuterie);
  }
  if (rep === undefined) {
    throw Object.assign(new Error('Session expirée : reconnectez-vous, l’envoi reprendra ensuite.'), { status: 401 });
  }
  return rep;
};

/** Fonctions d'appel au serveur liées au tenant, au format attendu par la file. */
export const apiPourTenant = (tenantSlug) => ({
  post: exigerReponse((endpoint, corps) => apiPost(tenantSlug, endpoint, corps)),
  put: exigerReponse((endpoint, corps) => apiPut(tenantSlug, endpoint, corps)),
  patch: exigerReponse((endpoint, corps) => apiPatch(tenantSlug, endpoint, corps)),
  del: exigerReponse((endpoint, corps) => apiDelete(tenantSlug, endpoint, corps)),
});

export { estErreurTransitoire, estIdLocal, nouvelIdLocal };
